import { createNode } from '@glueapp/graph-types';

export const FindInList = createNode({
    id: 'Find in List',
    name: 'Find in List',
    description: 'Find the first item in a list that matches a condition using a search function.',
    aiDescription: [
        'Finds the first item in a list matching a condition. Async — uses executeFunctionNode.',
        'Setup steps:',
        '1. Create a function with inputs "Item" (type T), "Index" (number), "List" (array of T) and output "Found" (boolean).',
        '   Inside the function, output true when the item matches your search criteria.',
        '2. Add this node: glue add "Find in List"',
        '3. Connect a list to "Input List".',
        '4. Connect the function node\'s output to the "Matcher" port (function type).',
        'Outputs: "Found" (boolean), "Found Item" (the matched item or null), "Found Index" (index or -1 if not found).',
    ].join('\n'),
    category: 'List',
    color: '#778ed5',
    isAsync: true,

    inPorts: {
        'Input List': {
            kind: 'array',
            subType: {
                kind: 'generic',
                identifier: 'T',
            },
        },
        Matcher: {
            kind: 'function',
            inputTypes: {
                Item: { kind: 'generic', identifier: 'T' },
                Index: { kind: 'number' },
                List: {
                    kind: 'array',
                    subType: { kind: 'generic', identifier: 'T' },
                },
            },
            outputTypes: {
                Found: { kind: 'boolean' },
            },
        },
    },

    outPorts: {
        Found: {
            kind: 'boolean',
        },
        'Found Item': {
            kind: 'generic',
            identifier: 'T',
        },
        'Found Index': {
            kind: 'number',
        },
    },

    execute: async ({ inPorts, executeFunctionNode }) => {
        if (!executeFunctionNode) {
            throw new Error('Function execution not available');
        }

        const list = inPorts['Input List'] as any[];
        const matcherValue = inPorts['Matcher'];

        // Check if the matcher is a FunctionReferenceValue
        if (
            !matcherValue ||
            typeof matcherValue !== 'object' ||
            !('isFunctionReferenceValue' in matcherValue) ||
            !matcherValue.isFunctionReferenceValue
        ) {
            throw new Error('Matcher must be a function reference');
        }

        const matcher = matcherValue as any; // Type as FunctionReferenceValue

        if (!list || !Array.isArray(list)) {
            return {
                output: {
                    Found: false,
                    'Found Item': null,
                    'Found Index': -1,
                },
            };
        }

        for (let i = 0; i < list.length; i++) {
            const item = list[i];

            // Execute the matcher function using the function ID from the reference
            const result = await executeFunctionNode(matcher.functionId, {
                Item: item,
                Index: i,
                List: list,
            });

            const isMatch = Boolean(result.output.Found);

            if (isMatch) {
                return {
                    output: {
                        Found: true,
                        'Found Item': item,
                        'Found Index': i,
                    },
                };
            }
        }

        // No match found
        return {
            output: {
                Found: false,
                'Found Item': null,
                'Found Index': -1,
            },
        };
    },
});
